'use client'

import * as React from 'react'
import { cn } from '@/lib/utils'

export interface ScoreRingProps {
  /** Score from 0 to 100. */
  score: number
  /** Diameter of the ring in pixels. Defaults to 64. */
  size?: number
  /** Stroke width in pixels. Defaults to 8 for size >= 90, otherwise 6 (matches approved mock). */
  strokeWidth?: number
  /**
   * Explicit CSS color for the value stroke (e.g. `var(--color-lime)` or a hex value).
   * Takes precedence over `getColor`.
   */
  color?: string
  /**
   * Derives the stroke color from the (clamped) score — e.g. a tier helper like
   * `scoreTier(score).colorVar`. Ignored if `color` is set.
   */
  getColor?: (score: number) => string
  /** Color of the background track circle. Defaults to `var(--color-border)`. */
  trackColor?: string
  /** Show the trailing "%" suffix next to the center number. Default true. */
  showPercentSuffix?: boolean
  /** Replace the centered content entirely (e.g. an icon instead of a number). */
  label?: React.ReactNode
  className?: string
}

const DEFAULT_COLOR = 'var(--color-lime)'
const DEFAULT_TRACK_COLOR = 'var(--color-border)'

/**
 * SVG ring gauge used across Brand Vitals (hero score, section cards, drawer header).
 * Animates the stroke on mount/score-change via a two-rAF trick so the browser paints
 * the empty ring before transitioning to the target offset; `motion-reduce:transition-none`
 * makes that transition a no-op (instant jump) for users who prefer reduced motion.
 */
const ScoreRing = React.forwardRef<HTMLDivElement, ScoreRingProps>(
  (
    {
      score,
      size = 64,
      strokeWidth,
      color,
      getColor,
      trackColor = DEFAULT_TRACK_COLOR,
      showPercentSuffix = true,
      label,
      className,
    },
    ref
  ) => {
    const clamped = Math.max(0, Math.min(100, score))
    const sw = strokeWidth ?? (size >= 90 ? 8 : 6)
    const radius = (size - sw) / 2
    const circumference = 2 * Math.PI * radius
    const targetOffset = circumference * (1 - clamped / 100)
    const resolvedColor = color ?? getColor?.(clamped) ?? DEFAULT_COLOR

    const [offset, setOffset] = React.useState(circumference)

    React.useEffect(() => {
      let raf1 = 0
      let raf2 = 0
      // Reset to fully-empty first so a subsequent score change re-animates
      // from zero, then animate to the target on the next two frames.
      setOffset(circumference)
      raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => setOffset(targetOffset))
      })
      return () => {
        cancelAnimationFrame(raf1)
        cancelAnimationFrame(raf2)
      }
    }, [circumference, targetOffset])

    const fontSize = Math.round(size * 0.26)

    return (
      <div
        ref={ref}
        role="img"
        aria-label={`Score ${Math.round(clamped)} out of 100`}
        className={cn('relative inline-flex flex-none items-center justify-center', className)}
        style={{ width: size, height: size }}
      >
        <svg width={size} height={size} className="block -rotate-90" aria-hidden="true">
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={trackColor}
            strokeWidth={sw}
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={resolvedColor}
            strokeWidth={sw}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            className="transition-[stroke-dashoffset] duration-[900ms] ease-[cubic-bezier(.22,1,.36,1)] motion-reduce:transition-none"
          />
        </svg>
        <div
          aria-hidden="true"
          className="absolute inset-0 flex items-center justify-center font-semibold tabular-nums tracking-tight text-[var(--color-text-primary)]"
          style={{ fontSize }}
        >
          {label ?? (
            <>
              {Math.round(clamped)}
              {showPercentSuffix && (
                <small className="ml-px text-[0.55em] font-medium text-[var(--color-text-muted)]">
                  %
                </small>
              )}
            </>
          )}
        </div>
      </div>
    )
  }
)
ScoreRing.displayName = 'ScoreRing'

export { ScoreRing }
